import { t } from 'i18next';
import _ from 'lodash';
import { sqStoredTableStore } from '@/core/core.stores';
import { refreshStoredTables, updateStoredTable } from '@/itemFinder/itemFinder.actions';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { setAutoFocusStoredTableTab, setStoredTableEditorVisible } from '@/main/layout.actions';
import { sqDatasourcesApi } from '@/sdk/api/DatasourcesApi';
import { sqStoredTablesApi } from '@/sdk/api/StoredTablesApi';
import { ColumnTypeEnum } from '@/sdk/model/StoredTableColumnOutputV1';
import {
  resetStoredTable,
  setIsLoadingStoredTable,
  setMaterializedTable,
  setStoredTable,
  setStoredTableName,
  setTableId,
} from '@/storedTableEditor/storedTable.actions';
import { filterPropertyAndUomColumns } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import { UOM_COLUMN_MATCH_SUFFIX } from '@/tableDefinitionEditor/tableDefinition.constants';
import { type BooleanTableCell, type NumericTableCell } from '@/tableDefinitionEditor/tableDefinition.types';
import { getMaterializedTable, updateTableDefinitionName } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { toNumber } from '@/utilities/numberHelper.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { setDisplayStoredTableEditor } from '@/worksheet/worksheet.actions';
import type { CellValueChangedEvent, ColDef, Column } from 'ag-grid-enterprise';
import type { ColumnDataUpdateInputListV1 } from '@/sdk/model/ColumnDataUpdateInputListV1';
import type { ColumnDataUpdateInputV1 } from '@/sdk/model/ColumnDataUpdateInputV1';
import type { DatasourceOutputV1 } from '@/sdk/model/DatasourceOutputV1';
import type { StoredTableColumnInputV1 } from '@/sdk/model/StoredTableColumnInputV1';
import type { StoredTableColumnOutputV1 } from '@/sdk/model/StoredTableColumnOutputV1';
import type {
  MaterializedTableCell,
  MaterializedTableHeader,
  MaterializedTableOutput,
  MaterializedTableRow,
  ProcessedMaterializedTable,
  ProcessedTableCell,
  TextTableCell,
} from '@/tableDefinitionEditor/tableDefinition.types';

export const STORED_TABLE_CANCELLATION_GROUP = 'stored-table';

export async function getExistingStoredTable(storedTableId: string) {
  try {
    setIsLoadingStoredTable(true);
    const { data: storedTableData } = await sqStoredTablesApi.getStoredTable({ id: storedTableId });
    setStoredTable(storedTableData);

    const output = await getMaterializedTable(
      storedTableId,
      storedTableData.columns.map((column) => column.columnName),
      [],
      STORED_TABLE_CANCELLATION_GROUP,
    );
    const newTable: MaterializedTableOutput | undefined = output ? output : undefined;
    setMaterializedTable(newTable);
    setIsLoadingStoredTable(false);

    return storedTableData;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}

export function editStoredTable(storedTableId: string) {
  setAutoFocusStoredTableTab(true);
  setDisplayStoredTableEditor(true);
  resetStoredTable();
  setTableId(storedTableId);
}

export const buildRowDataForAgGrid = (
  agGridColumnDefinitions: ColDef[],
  materializedTable: ProcessedMaterializedTable | undefined,
) => {
  if (!materializedTable) {
    return null;
  }

  const materializedTableHeaders = materializedTable.headers;

  const rows = materializedTable.rows;
  return rows.map((row) => {
    const rowData: Record<string, any> = {};
    materializedTableHeaders.forEach((header: any, index: number) => {
      const agColumn = agGridColumnDefinitions.find((columnDef) => columnDef.headerName === header.name);
      const field = agColumn?.field;
      if (field) {
        rowData[field] = row[index];
      }
    });
    return rowData;
  });
};

export const handleCellValueChanged = async (
  event: CellValueChangedEvent,
  columns: StoredTableColumnOutputV1[],
  tableId: string | undefined,
) => {
  const uniqueIdColumn = columns.find((c) => c.columnName === SeeqNames.MaterializedTables.UniqueRowId)?.id;
  const column = columns.find((c) => c.columnName === event.colDef.headerName);
  const dataUpdates: ColumnDataUpdateInputV1[] = [];

  if (column && uniqueIdColumn && tableId) {
    const updateValue = event.data[column.id].value;
    const rowIdValue = event.data[uniqueIdColumn]?.value;
    const updateUom = event.data[column.id].uom;

    if (rowIdValue === undefined || updateValue === undefined) {
      resetValue(event, column);
      errorToast({ messageKey: 'STORED_TABLE.UNABLE_TO_UPDATE_CELL' });
      return;
    }

    dataUpdates.push({
      rowId: rowIdValue,
      value: updateValue,
      uom: updateUom,
    });
    const input: ColumnDataUpdateInputListV1 = { dataUpdates };

    try {
      await sqStoredTablesApi.updateColumnData(input, {
        id: tableId,
        columnId: column.id,
      });

      successToast({ messageKey: 'STORED_TABLE.DATA_UPDATED' });
      await getExistingStoredTable(tableId);
    } catch (e) {
      resetValue(event, column);
      errorToast({ httpResponseOrError: e });
    }
  } else {
    errorToast({ messageKey: 'STORED_TABLE.UNABLE_TO_UPDATE_CELL' });
  }
  event.api.clearFocusedCell();
};

function resetValue(event: CellValueChangedEvent, column: StoredTableColumnOutputV1) {
  event.data[column.id] = event.oldValue;
  event.api.refreshCells({ columns: [event.column], rowNodes: [event.node] });
}

export const processMaterializedTable = (
  rawMaterializedTable: MaterializedTableOutput | undefined,
  columns: StoredTableColumnOutputV1[],
): ProcessedMaterializedTable | undefined => {
  if (!rawMaterializedTable) {
    return undefined;
  }
  const rows = rawMaterializedTable.rows;
  const headers = rawMaterializedTable.headers;

  const processedRows: ProcessedTableCell[][] = rows.map((row) => {
    return mapRowToProcessedCells(row, headers, columns);
  });

  return filterPropertyAndUomColumns(headers, processedRows, rawMaterializedTable.hasMore);
};

const mapRowToProcessedCells = (
  row: MaterializedTableRow,
  headers: MaterializedTableHeader[],
  columns: StoredTableColumnOutputV1[],
): ProcessedTableCell[] => {
  return row.map((cellValue, columnIndex) => {
    const header = headers[columnIndex];
    const column = columns.find((col) => col.columnName === header.name);

    return {
      override: false,
      ...processCellByType({ cellValue, row, header, headers, column }),
    };
  });
};

const processCellByType = (processCellProps: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  header: MaterializedTableHeader;
  headers: MaterializedTableHeader[];
  column: StoredTableColumnOutputV1 | undefined;
}) => {
  if (_.isNil(processCellProps.cellValue)) {
    return {
      value: undefined,
    };
  }

  switch (processCellProps.header.type) {
    case ColumnTypeEnum.NUMERIC:
      return processNumericCell(processCellProps);
    case ColumnTypeEnum.BOOLEAN:
      return processBooleanCell(processCellProps.cellValue);
    default: {
      return processDefaultCell(processCellProps.cellValue);
    }
  }
};

const processDefaultCell = (cellValue: MaterializedTableCell) => {
  return {
    value: cellValue ? cellValue.toString() : undefined,
  } as TextTableCell;
};

const processNumericCell = ({
  cellValue,
  row,
  header,
  headers,
  column,
}: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  header: MaterializedTableHeader;
  headers: MaterializedTableHeader[];
  column: StoredTableColumnOutputV1 | undefined;
}) => {
  const headerMap = headers.reduce((map, header) => {
    const headerName = header.name ?? '';
    if (headerName) {
      map.set(headerName, header);
    }
    return map;
  }, new Map<string, MaterializedTableHeader>());

  const potentialNumericValue = toNumber(cellValue);
  const headerName = header.name ?? '';
  const potentialUomColumn = headerName ? headerMap.get(`${headerName}${UOM_COLUMN_MATCH_SUFFIX}`) : undefined;
  const potentialUomColumnIndex = potentialUomColumn?.name
    ? headers.findIndex((h) => h.name === potentialUomColumn.name)
    : -1;
  const potentialUomColumnValue = potentialUomColumnIndex > -1 ? row[potentialUomColumnIndex] : undefined;
  // If there was not a uom column, then check the column definition for a uom override
  const uomColumnValue = potentialUomColumnValue ?? column?.columnUom;

  if (_.isNil(potentialNumericValue)) {
    return processDefaultCell(cellValue);
  } else {
    return {
      value: potentialNumericValue,
      uom: uomColumnValue as string | undefined,
    } as Partial<NumericTableCell>;
  }
};

const processBooleanCell = (cellValue: MaterializedTableCell) => {
  return {
    value: !!cellValue,
  } as Partial<BooleanTableCell>;
};

export const onTableReload = (newTable?: MaterializedTableOutput) => {
  setMaterializedTable(newTable);
};

export const storedTableColumnOutputToInput = (column: StoredTableColumnOutputV1): StoredTableColumnInputV1 => {
  return {
    columnType: column.columnType,
    columnUom: column.columnUom,
    columnName: column.columnName,
    isIndexed: column.isIndexed,
    isHidden: column.isHidden,
    sortAscending: column.sortAscending,
    sortIndex: column.sortIndex,
    description: column.description,
  };
};

export const closeStoredTableEditor = () => {
  setDisplayStoredTableEditor(false);
  setStoredTableEditorVisible(false);
};

export const deleteStoredTableColumn = async (column: StoredTableColumnOutputV1, tableId: string) => {
  try {
    await sqStoredTablesApi.deleteColumnFromStoredTable({ id: tableId, columnId: column.id });
    await getExistingStoredTable(tableId);
    await updateStoredTable(tableId);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * Retrieves the data source for a table definition.
 */
export const getStoredTableDataSource = async (storedTableId: string): Promise<DatasourceOutputV1 | undefined> => {
  const {
    data: { datasources },
  } = await sqDatasourcesApi.getDatasources({
    datasourceId: storedTableId.toLocaleLowerCase(),
  });

  return datasources?.at(0);
};

export const handleUpdateTableName = async (newName: string | undefined, tableId: string | undefined) => {
  const trimmedTableName = newName?.trim();
  if (trimmedTableName) {
    if (tableId && trimmedTableName !== sqStoredTableStore.name) {
      try {
        await updateTableDefinitionName({ tableDefinitionId: tableId, name: trimmedTableName });
        setStoredTableName(trimmedTableName);
        await refreshStoredTables();
      } catch (e) {
        errorToast({ messageKey: t('ITEM_FINDER.UPDATE_NAME_TABLE_DEFINITION_ERROR') });
      }
    }
  }
};

export const deleteStoredTableRows = async (tableId: string, rowIds: string[]) => {
  try {
    const response = await sqStoredTablesApi.deleteStoredTableRows(
      {
        rowIds,
      },
      {
        tableId,
      },
    );
    return response.data;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
    return { deletedRowCount: 0 };
  }
};

export const reorderColumns = async (
  tableId: string,
  columns: StoredTableColumnOutputV1[],
  agGridColumns: Column[],
  affectedColumnId: string,
  toIndex: number,
) => {
  const columnsIdsNotInGrid = columns
    .filter((column) => !agGridColumns.find((agGridColumn) => agGridColumn.getColDef().field === column.id))
    .map((column) => column.id);

  const columnsIdsInGridInNewOrder = agGridColumns
    .map((agColumn) => agColumn.getColDef().field)
    .filter((columnId) => columnId !== affectedColumnId)
    .filter((columnId): columnId is string => columnId !== undefined);

  columnsIdsInGridInNewOrder.splice(toIndex, 0, affectedColumnId);
  const newOrder = columnsIdsNotInGrid.concat(columnsIdsInGridInNewOrder);
  try {
    const { data: updatedStoredTable } = await sqStoredTablesApi.reorderColumnsInStoredTable(
      { columnDefinitionIds: newOrder },
      { id: tableId },
    );
    setStoredTable(updatedStoredTable);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};
